import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useMockLocationStore } from "../stores/mockLocation";

const PERMISSION_STORAGE_KEY = "location-permission";

// Dev-only: Parse mock location from URL params
// Usage: ?mock_lat=29.9511&mock_lng=-90.0715&mock_accuracy=10
function getMockLocation(): { lat: number; lng: number; accuracy: number } | null {
  if (!import.meta.env.DEV) return null;
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const mockLat = params.get("mock_lat");
  const mockLng = params.get("mock_lng");

  if (mockLat && mockLng) {
    const lat = parseFloat(mockLat);
    const lng = parseFloat(mockLng);
    const accuracy = parseFloat(params.get("mock_accuracy") || "10");

    if (!isNaN(lat) && !isNaN(lng)) {
      console.log(`[DEV] Using mock location: ${lat}, ${lng} (accuracy: ${accuracy}m)`);
      return { lat, lng, accuracy };
    }
  }
  return null;
}

export interface UserLocation {
  coords: {
    latitude: number;
    longitude: number;
  };
  accuracy: number;
  timestamp: number;
}

export interface UseUserLocationReturn {
  location: UserLocation | null;
  error: GeolocationPositionError | null;
  isWatching: boolean;
  permissionState: PermissionState | null;
  startWatching: () => void;
  stopWatching: () => void;
}

function getStoredPermission(): PermissionState | null {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem(PERMISSION_STORAGE_KEY);
  if (stored === "granted" || stored === "denied" || stored === "prompt") {
    return stored;
  }
  return null;
}

function storePermission(state: PermissionState): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(PERMISSION_STORAGE_KEY, state);
}

export function useUserLocation(): UseUserLocationReturn {
  const [realLocation, setRealLocation] = useState<UserLocation | null>(null);
  const [error, setError] = useState<GeolocationPositionError | null>(null);
  const [isWatching, setIsWatching] = useState(false);
  const [permissionState, setPermissionState] = useState<PermissionState | null>(
    () => getStoredPermission()
  );
  const watchIdRef = useRef<number | null>(null);
  const mockLocationStore = useMockLocationStore((s) => s.mockLocation);

  // Use mock location if set, otherwise use real location
  const location = useMemo((): UserLocation | null => {
    if (mockLocationStore) {
      return {
        coords: {
          latitude: mockLocationStore.latitude,
          longitude: mockLocationStore.longitude,
        },
        accuracy: 5, // Mock accuracy
        timestamp: Date.now(),
      };
    }
    return realLocation;
  }, [mockLocationStore, realLocation]);

  // Query and track permission state
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions) return;

    let permissionStatus: PermissionStatus | null = null;

    const handleChange = () => {
      if (permissionStatus) {
        setPermissionState(permissionStatus.state);
        storePermission(permissionStatus.state);
      }
    };

    navigator.permissions
      .query({ name: "geolocation" })
      .then((status) => {
        permissionStatus = status;
        setPermissionState(status.state);
        storePermission(status.state);
        status.addEventListener("change", handleChange);
      })
      .catch(() => {
        // Permissions API not supported, rely on stored value
      });

    return () => {
      if (permissionStatus) {
        permissionStatus.removeEventListener("change", handleChange);
      }
    };
  }, []);

  const startWatching = useCallback(() => {
    // Dev-only: Check for mock location in URL params
    const mockLocation = getMockLocation();
    if (mockLocation) {
      setRealLocation({
        coords: {
          latitude: mockLocation.lat,
          longitude: mockLocation.lng,
        },
        accuracy: mockLocation.accuracy,
        timestamp: Date.now(),
      });
      setPermissionState("granted");
      setIsWatching(true);
      return;
    }

    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return;
    }

    if (watchIdRef.current !== null) {
      return; // Already watching
    }

    setError(null);
    setIsWatching(true);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        setRealLocation({
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          },
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        });
        setError(null);
      },
      (err) => {
        setError(err);
        // Update permission state on error
        if (err.code === err.PERMISSION_DENIED) {
          setPermissionState("denied");
          storePermission("denied");
        }
      },
      {
        enableHighAccuracy: true,
      }
    );
  }, []);

  const stopWatching = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setIsWatching(false);
    }
  }, []);

  // Auto-start if mock location params present (dev only)
  useEffect(() => {
    const mockLocation = getMockLocation();
    if (mockLocation && !isWatching) {
      startWatching();
    }
  }, [startWatching, isWatching]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  return {
    location,
    error,
    isWatching,
    permissionState,
    startWatching,
    stopWatching,
  };
}
